import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import CommercialGallery from "./pages/CommercialGallery";
import ResidentialGallery from "./pages/ResidentialGallery";
import Careers from "./pages/Careers";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Unsubscribe from "./pages/Unsubscribe";
import PaintersNashuaNH from "./pages/PaintersNashuaNH";
import PaintersManchesterNH from "./pages/PaintersManchesterNH";
import NotFound from "./pages/NotFound";
import ScrollToHash from "./components/ScrollToHash";
import PageViewTracker from "./components/PageViewTracker";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import plugins from "./plugins";

// Lazy-load admin surface so recharts/admin code stays out of the public bundle
const Login = lazy(() => import("./pages/admin/Login"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <ScrollToHash />
          <PageViewTracker />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/commercial" element={<CommercialGallery />} />
            <Route path="/residential" element={<ResidentialGallery />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/painters-nashua-nh" element={<PaintersNashuaNH />} />
            <Route path="/painters-manchester-nh" element={<PaintersManchesterNH />} />
            <Route
              path="/admin/login"
              element={
                <Suspense fallback={null}>
                  <Login />
                </Suspense>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Suspense fallback={null}>
                    <AdminLayout />
                  </Suspense>
                </ProtectedRoute>
              }
            >
              <Route
                index
                element={
                  <Suspense fallback={null}>
                    <Dashboard />
                  </Suspense>
                }
              />
              {plugins.map((plugin) => (
                <Route
                  key={plugin.id}
                  path={plugin.route}
                  element={
                    <Suspense fallback={null}>
                      <plugin.component />
                    </Suspense>
                  }
                />
              ))}
            </Route>
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
